import {HttpHeaders} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {AuthService} from '../services/authentication/auth.service';
import CryptoJS from 'crypto-js';
import GlobalComponent from '../../global-component';

@Injectable({ providedIn: 'root' })
export class AuthHelper {
  private auth = inject(AuthService);
  /**
   * Helper: Get Unix timestamp (seconds).
   */
  public getUnixTimestampSeconds(): string {
    return Math.floor(Date.now() / 1000).toString();
  }


  /**
   * Helper: HMAC-SHA256(message, key = token) → hex string
   * Uses Web Crypto API (available in modern browsers).
   */
  async hmacSha256Hex(message: string, secret: string): Promise<string> {
    if (!secret || secret.trim().length === 0) {
      throw new Error('Signing secret (token) is empty');
    }

    const hmac = CryptoJS.HmacSHA256(message, secret);
    return CryptoJS.enc.Hex.stringify(hmac);

    const enc = new TextEncoder();
    const key = await crypto.subtle.importKey(
      'raw',
      enc.encode(secret),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    const signature = await crypto.subtle.sign('HMAC', key, enc.encode(message));
    // Convert ArrayBuffer to hex
    const bytes = new Uint8Array(signature);
    return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
  }

  async buildSignedHeaders(isMultipart: boolean = false, base?: HttpHeaders): Promise<HttpHeaders> {
    const raw = this.auth.getAccessToken();
    const token = (raw ?? '').trim();
    if (!token) {
      // Option A: throw with clear message
      //throw new Error('Missing access token (empty). Ensure login completes before calling signed APIs.');

      // Option B: for public endpoints, skip signature (if allowed)
       return (base ?? new HttpHeaders()).set('Accept', 'application/json');
    }

    const ts = this.getUnixTimestampSeconds().toString();
    const message = GlobalComponent.AUTH_CLIENT_SECRET + ts;
    const sig = await this.hmacSha256Hex(message, GlobalComponent.AUTH_CLIENT_SECRET);

    let headers = base ?? new HttpHeaders();
    if(isMultipart) {
      headers = headers
        .delete('Content-Type')
        .set('Accept', 'text/plain')
        .set('X-Timestamp', ts)
        .set('X-Signature', sig);
    } else {
      headers = headers
        // .set('Content-Type', 'multipart/form-data')
        .set('Accept', 'application/json')
        .set('X-Timestamp', ts)
        .set('X-Signature', sig);
    }
    return headers;
  }
}
